import moment from "moment/min/moment-with-locales";
import React, { useMemo, useRef, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import DataDescription from "../components/DataDescription";
import { getRadioName, mapItemType } from "../components/HelperFunctions";
import {
    useFetchData,
    useLatestCalculatedDate,
    useSetDaypartOnChange,
    useSetDaysOnPerspectiveChange,
    useSetPerspectiveOnDaysChange,
    useResolvedRadios,
} from "../components/HelperHooks";
import PageDesciption from "../components/PageDesciption";
import DatesPicker, { latest2Weeks, latestDay, latestMonth, latestQuarter, latestWeek } from "../components/Pickers/DatesPicker";
import DaypartPicker from "../components/Pickers/DaypartPicker";
import DaysPicker from "../components/Pickers/DaysPicker";
import HoursPicker from "../components/Pickers/HoursPicker";
import MusicSelectionTypePicker from "../components/Pickers/MusicSelectionTypePicker";
import MinMaxSpinsPicker from "../components/Pickers/MinMaxSpinsPicker";
import PerspectivePicker from "../components/Pickers/PerspectivePicker";
import StationPicker from "../components/Pickers/StationPicker";
import SongOverlapChart from "../components/SongsOverlapChart";
import { useUserFormState } from "../components/UserSelection";
import ComparisonSongsTable from "../components/comparison/ComparisonSongsTable";
import ContentBalanceTrendChart from "../components/dashboard/ContentBalanceTrendChart";
import { RadioName } from "../components/helperRenders";
import { DashboardPanel, DataContent, DescriptionWithDownloadRow, InputSelection, ModulePage } from "../components/pageSections";
import ReleaseYearChart from "../components/ReleaseYearChart";
import MinMaxYearPicker from "../components/Pickers/MinMaxYearPicker";

const extraDefaults = { station2: [], selectionType: "all", minSpins: 0, maxSpins: 0, minYear: 0, maxYear: new Date().getFullYear() };

export default function Comparison() {
    const moduleName = useMemo(() => "comparison", []);
    const moduleId = useMemo(() => ({ 0: 38, 1: 38, 2: 38 }), []);

    const [formData, setFormData, saveFormData, loadFormData] = useUserFormState(moduleName, extraDefaults);
    const [comparisonLoading, comparisonErrorCode, comparisonData, fetchComparisonData] = useFetchData();
    const [contentBalanceLoading, contentBalanceErrorCode, contentBalanceData, fetchContentBalanceData] = useFetchData();
    const [latestCalculatedDate, setLatestCalculatedDate] = useState(null);
    const resolvedRadios = useResolvedRadios(true);
    const advancedInputRef = useRef(null);
    const contentBalanceRef = useRef(null);
    const overlapRef = useRef(null);
    const decadesBreakdownRef = useRef(null);
    const [fileName, setFileName] = useState("Content Balance");
    const [fileNameOverlap, setFileNameOverlap] = useState("Song Overlap");
    const [grouping, setGrouping] = useState(false);

    const predefinedRanges = useMemo(() => {
        if (latestCalculatedDate) {
            if (formData.dateRange === undefined) {
                setFormData(current => ({ ...current, dateRange: latestWeek(latestCalculatedDate, true) }));
            }
            return [
                latestDay(latestCalculatedDate),
                latestWeek(latestCalculatedDate),
                latest2Weeks(latestCalculatedDate),
                latestMonth(latestCalculatedDate),
                latestQuarter(latestCalculatedDate),
            ];
        }
    }, [formData.dateRange, latestCalculatedDate, setFormData]);

    const demo = useMemo(() => [{ data_type: 0, listener_group: 0 }], []);
    useLatestCalculatedDate(setLatestCalculatedDate, moduleId, formData.station, demo);
    useSetPerspectiveOnDaysChange(setFormData, formData.days);
    useSetDaysOnPerspectiveChange(setFormData, formData.perspective);
    useSetDaypartOnChange(setFormData, formData.station, formData.days, formData.hourRange);

    async function handleFormSubmit(event) {
        event.preventDefault();
        advancedInputRef.current.closeAdvanced();
        await saveFormData();
        await saveFormData(formData.station[0].radio_id);
        const radioOne = formData.station[0]?.radio_id ?? null;
        const radioTwo = formData.station2?.[0]?.radio_id ?? null;
        const input = {
            radio_ids: [radioOne, radioTwo].filter(id => id !== null),
            start_datetime: moment(formData.dateRange[0]).startOf("day").format("YYYY-MM-DDTHH:mm:ss"),
            end_datetime: moment(formData.dateRange[1]).endOf("day").format("YYYY-MM-DDTHH:mm:ss"),
            start_time: moment().hours(formData.hourRange[0]).minutes(0).seconds(0).format("HH:mm:ss"),
            end_time: moment()
                .hours(formData.hourRange[1] - 1)
                .minutes(59)
                .seconds(59)
                .format("HH:mm:ss"),
            days: formData.days,
            limit: formData.selectionType === "top" ? 50 : 0,
            new: formData.selectionType === "new",
            min_spins: formData.minSpins,
            max_spins: formData.maxSpins,
            min_release_year: formData.minYear,
            max_release_year: formData.maxYear,
        };

        await fetchComparisonData("comparison/", input);
        await fetchContentBalanceData("comparison/content_balance/", input);
    }

    const csvHeaders = useMemo(() => {
        if (!resolvedRadios || !contentBalanceData) return;

        const firstRadio = getRadioName(contentBalanceData.balances[0].radios[0].radio_id, resolvedRadios);
        const secondRadio = contentBalanceData.balances[0].radios[1] ? getRadioName(contentBalanceData.balances[0].radios[1].radio_id, resolvedRadios) : null;

        setFileName(
            `Content Balance for ${firstRadio}${secondRadio ? " and " + secondRadio : ""} from ${moment(formData.dateRange[0]).format(
                "DD-MM-YYYY"
            )} to ${moment(formData.dateRange[1]).format("DD-MM-YYYY")}`
        );
        setFileNameOverlap(
            `Song Overlap for ${firstRadio}${secondRadio ? " and " + secondRadio : ""} from ${moment(formData.dateRange[0]).format("DD-MM-YYYY")} to ${moment(
                formData.dateRange[1]
            ).format("DD-MM-YYYY")} `
        );

        const headers = [
            "Date",
            ...contentBalanceData.balances[0].radios[0].balances.map(x => `${firstRadio} ${mapItemType(x.item_type)}`),
            ...(secondRadio ? contentBalanceData.balances[0].radios[1].balances.map(x => `${secondRadio} ${mapItemType(x.item_type)}`) : []),
        ];

        return headers;
    }, [contentBalanceData, resolvedRadios]);

    const csvData = useMemo(() => {
        if (!contentBalanceData) return;

        return contentBalanceData.balances.map(({ date, radios }) => {
            const [firstRadio, secondRadio] = radios;

            const firstRadioBalances = firstRadio.balances.map(x => `${x.value.toFixed(2)}%`);
            const secondRadioBalances = secondRadio ? secondRadio.balances.map(x => `${x.value.toFixed(2)}%`) : [];

            return [moment(date).format("DD-MM-YYYY"), ...firstRadioBalances, ...secondRadioBalances];
        });
    }, [contentBalanceData]);

    const handleGroupingChange = value => {
        setGrouping(value);
    };

    const memoizedChart = useMemo(() => {
        if (!comparisonData) return null;
        return (
            <ReleaseYearChart
                data={comparisonData.common_songs}
                grouping={grouping}
                loading={comparisonLoading}
                error={comparisonErrorCode}
                isComparison={true}
            />
        );
    }, [comparisonData, grouping, comparisonLoading, comparisonErrorCode]);

    return (
        <ModulePage>
            <ModulePage.Title>Comparison</ModulePage.Title>
            <ModulePage.InputSelection handleFormSubmit={handleFormSubmit} disabled={!formData.station.length || !formData.dateRange || !formData.days.length}>
                <InputSelection.Main>
                    <StationPicker
                        formData={formData}
                        setFormData={setFormData}
                        name={moduleName}
                        includeCompetitors={true}
                        label="Station 1"
                        loadOnChange={loadFormData}
                        colXl={2}
                    />
                    <StationPicker
                        formData={formData}
                        setFormData={setFormData}
                        name={moduleName}
                        disabled={!formData.station.length}
                        includeCompetitors={true}
                        formKey="station2"
                        label="Station 2"
                        colXl={2}
                    />
                    <DatesPicker formData={formData} setFormData={setFormData} predefinedRanges={predefinedRanges} latestAvailableDate={latestCalculatedDate} />
                    <DaypartPicker formData={formData} setFormData={setFormData} name={moduleName} />
                    <MusicSelectionTypePicker formData={formData} setFormData={setFormData} name={moduleName} />
                </InputSelection.Main>
                <InputSelection.Advanced moduleName={moduleName} ref={advancedInputRef}>
                    <DaysPicker formData={formData} setFormData={setFormData} name={moduleName} />
                    <HoursPicker formData={formData} setFormData={setFormData} name={moduleName} />
                    <PerspectivePicker formData={formData} setFormData={setFormData} name={moduleName} />
                    <MinMaxYearPicker filterData={formData} setFilterData={setFormData} name={moduleName} />
                    <MinMaxSpinsPicker filterData={formData} setFilterData={setFormData} name={moduleName} />
                </InputSelection.Advanced>
            </ModulePage.InputSelection>
            <ModulePage.DataContent loading={comparisonLoading || contentBalanceLoading} errorCode={comparisonErrorCode ?? contentBalanceErrorCode} multiPanel>
                <DataContent.Placeholder show={!comparisonData && !contentBalanceData}>
                    <PageDesciption
                        title={"Comparison"}
                        className="bg-white p-4 px-4"
                        whatList={[
                            "Compare any two stations with each other",
                            "Music and talk percentages for the timeframe selected are displayed on top",
                            "Library comparisons with overlap percentages, and lists of unique and common songs are displayed below",
                            "Filter by specific days or dayparts, or by type of song – new music, top 50, all songs",
                        ]}
                        knowList={[
                            { text: "Filter for specific song titles, artists, or just words in the search bar above each of the tables" },
                            { text: "Clicking on a song opens up a station by station breakdown" },
                            {
                                text: "Comparison is a great way to find unique titles to highlight in hook promos or to check up on what makes your station format unique compared to others in your genre or market",
                            },
                        ]}
                        relatedLinks={[
                            {
                                title: "Tactical Observation",
                                url: "https://radioanalyzer.com/blog/tactical-observation",
                            },
                        ]}
                        videoSrc="https://www.youtube.com/embed/LTvc4ahAt58?si=Kt1-jZ7HBX_aPPx-"
                    />
                </DataContent.Placeholder>
                <Container fluid>
                    {contentBalanceData && (
                        <>
                            <DashboardPanel containerRef={contentBalanceRef}>
                                <Col xs={12} xl={12}>
                                    <DashboardPanel.Title>Content Balance</DashboardPanel.Title>
                                    <DescriptionWithDownloadRow
                                        csvHeaders={csvHeaders}
                                        data={csvData}
                                        filename={fileName}
                                        screenshotContainer={contentBalanceRef}>
                                        <DashboardPanel.Description fileName="Content Balance Trend" dataReady={!!contentBalanceData}>
                                            <DataDescription>
                                                {contentBalanceData.input.radio_ids.length < 2 ? (
                                                    <DataDescription.Station radioId={contentBalanceData.input.radio_ids[0]} />
                                                ) : (
                                                    <>
                                                        <DataDescription.Station label="Station 1" radioId={contentBalanceData.input.radio_ids[0] ?? ""} />
                                                        <DataDescription.Station label="Station 2" radioId={contentBalanceData.input.radio_ids[1] ?? ""} />
                                                    </>
                                                )}
                                                <DataDescription.DateRange
                                                    startDate={contentBalanceData?.input.start_datetime}
                                                    endDate={contentBalanceData.input?.end_datetime}
                                                />
                                                <DataDescription.TimeRange
                                                    startTime={contentBalanceData.input?.start_time}
                                                    endTime={contentBalanceData.input?.end_time}
                                                />
                                                <DataDescription.Perspective days={contentBalanceData.input?.days} />
                                            </DataDescription>
                                        </DashboardPanel.Description>
                                    </DescriptionWithDownloadRow>
                                    <DashboardPanel.DataContent>
                                        <ContentBalanceTrendChart
                                            data={contentBalanceData.balances}
                                            loading={contentBalanceLoading}
                                            error={contentBalanceErrorCode}
                                            grouping={contentBalanceData.grouping}
                                        />
                                    </DashboardPanel.DataContent>
                                </Col>
                            </DashboardPanel>
                            <hr className="m-2" />
                        </>
                    )}
                    {comparisonData && comparisonData?.unique_songs[0] && comparisonData?.unique_songs[1] && (
                        <>
                            <DashboardPanel containerRef={overlapRef}>
                                <Col xs={12} xl={12}>
                                    <DashboardPanel.Title>Songs Overlap</DashboardPanel.Title>
                                    <DashboardPanel.Description containerRef={overlapRef} fileName={fileNameOverlap} dataReady={!!comparisonData}>
                                        <DataDescription>
                                            <DataDescription.Station label="Station 1" radioId={comparisonData.input.radio_ids[0]} />
                                            <DataDescription.Station label="Station 2" radioId={comparisonData.input.radio_ids[1]} />{" "}
                                            <DataDescription.DateRange
                                                startDate={comparisonData?.input.start_datetime}
                                                endDate={comparisonData.input?.end_datetime}
                                            />
                                            <DataDescription.TimeRange startTime={comparisonData.input?.start_time} endTime={comparisonData.input?.end_time} />
                                            <DataDescription.Perspective days={comparisonData.input?.days} />
                                            {(comparisonData.input.min_release_year > 0 ||
                                                (comparisonData.input.max_release_year > 0 &&
                                                    comparisonData.input.max_release_year !== new Date().getFullYear())) && (
                                                <DataDescription.Simple
                                                    label="Release Year"
                                                    value={`${comparisonData.input.min_release_year} - ${comparisonData.input.max_release_year}`}
                                                />
                                            )}
                                            {(comparisonData.input?.min_spins > 0 || comparisonData.input?.max_spins > 0) && (
                                                <DataDescription.Simple
                                                    label="Spins"
                                                    value={`${comparisonData.input?.min_spins} - ${comparisonData.input?.max_spins || "∞"}`}
                                                />
                                            )}
                                        </DataDescription>
                                    </DashboardPanel.Description>
                                    <DashboardPanel.DataContent>
                                        <SongOverlapChart
                                            data={comparisonData}
                                            loading={comparisonLoading}
                                            error={comparisonErrorCode}
                                            moduleName={moduleName}
                                        />
                                    </DashboardPanel.DataContent>
                                </Col>
                            </DashboardPanel>
                            <hr className="m-2" />
                        </>
                    )}

                    {comparisonData?.common_songs?.length > 0 && (
                        <>
                            <DashboardPanel>
                                <DashboardPanel.Title>{comparisonData?.common_songs?.length} Common Songs</DashboardPanel.Title>
                                <DashboardPanel.DataContent>
                                    <ComparisonSongsTable comparisonData={comparisonData} songs={comparisonData?.common_songs} isCommon={true} radioId={[]} />
                                </DashboardPanel.DataContent>
                            </DashboardPanel>
                            <hr className="m-2"></hr>
                        </>
                    )}
                    <Row>
                        {comparisonData?.unique_songs[0].songs && (
                            <Col xs={12} xl={!comparisonData?.unique_songs[1]?.songs ? 12 : 6}>
                                <DashboardPanel>
                                    <DashboardPanel.Title>
                                        {comparisonData?.unique_songs[0].songs.length} Unique Songs - <RadioName radioId={comparisonData.input.radio_ids[0]} />
                                    </DashboardPanel.Title>
                                    <DashboardPanel.DataContent>
                                        <ComparisonSongsTable
                                            comparisonData={comparisonData}
                                            songs={comparisonData?.unique_songs[0].songs}
                                            isCommon={false}
                                            radioId={comparisonData?.input.radio_ids[0]}
                                        />
                                    </DashboardPanel.DataContent>
                                </DashboardPanel>
                            </Col>
                        )}
                        {comparisonData?.unique_songs[1]?.songs && (
                            <>
                                <hr className="m-2 d-xl-none" />
                                <Col xs={12} xl={6}>
                                    <DashboardPanel>
                                        <DashboardPanel.Title>
                                            {comparisonData?.unique_songs[1].songs.length} Unique Songs -{" "}
                                            <RadioName radioId={comparisonData.input.radio_ids[1]} />
                                        </DashboardPanel.Title>
                                        <DashboardPanel.DataContent>
                                            <ComparisonSongsTable
                                                comparisonData={comparisonData ?? ""}
                                                songs={comparisonData?.unique_songs[1].songs}
                                                isCommon={false}
                                                radioId={comparisonData?.input.radio_ids[1]}
                                            />
                                        </DashboardPanel.DataContent>
                                    </DashboardPanel>
                                </Col>
                            </>
                        )}
                    </Row>
                </Container>
            </ModulePage.DataContent>
        </ModulePage>
    );
}
